/**
 * SocialConnectionPanel — Configuración → Redes sociales.
 *
 * Buffer no es una red social: es el puente que administra las cuentas.
 * Eso se aclara en la interfaz para que nadie lo confunda.
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  AlertTriangle, CheckCircle2, ExternalLink, Link2, Loader2,
  RefreshCw, Unlink,
} from 'lucide-react';
import { toast } from 'sonner';
import { socialService } from '../../services/socialService';
import { CHANNEL_STATUS_LABEL, PlatformIcon, platformMeta } from './platforms';

export const SocialConnectionPanel = () => {
  const [state, setState] = useState({ loading: true, data: null, error: null });
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    setState((s) => ({ ...s, loading: true }));
    try {
      const data = await socialService.status();
      setState({ loading: false, data, error: null });
    } catch (err) {
      setState({
        loading: false, data: null,
        error: err?.response?.data?.detail || 'No pudimos leer el estado de la conexión.',
      });
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const result = params.get('social');
    if (!result) return;
    const messages = {
      connected: ['success', 'Cuenta de Buffer conectada'],
      denied: ['error', 'Cancelaste la autorización en Buffer'],
      invalid: ['error', 'El enlace de autorización no era válido'],
      failed: ['error', 'Buffer rechazó la autorización. Probá de nuevo.'],
      migration_missing: ['error', 'Falta aplicar la migración 019 en Supabase'],
      error: ['error', 'Ocurrió un error al conectar con Buffer'],
    };
    const [kind, message] = messages[result] || ['info', 'Volviste de Buffer'];
    toast[kind === 'success' ? 'success' : kind === 'error' ? 'error' : 'info'](message);
    window.history.replaceState({}, '', window.location.pathname + '?tab=social');
  }, []);

  const connect = async () => {
    setBusy('connect');
    try {
      const { authorization_url } = await socialService.connect('/settings');
      window.location.href = authorization_url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'No pudimos iniciar la conexión con Buffer.');
      setBusy(null);
    }
  };

  const sync = async () => {
    setBusy('sync');
    try {
      await socialService.syncChannels();
      await load();
      toast.success('Canales sincronizados');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'No pudimos sincronizar los canales.');
    } finally { setBusy(null); }
  };

  const disconnect = async () => {
    if (!window.confirm('¿Desconectar tu cuenta de Buffer? Las publicaciones ya programadas siguen en Buffer.')) return;
    setBusy('disconnect');
    try {
      await socialService.disconnect();
      await load();
      toast.success('Cuenta desconectada');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'No pudimos desconectar la cuenta.');
    } finally { setBusy(null); }
  };

  if (state.loading) {
    return (
      <div className="social-loading" data-testid="social-loading">
        <Loader2 size={18} className="animate-spin" /> Cargando tus redes…
      </div>
    );
  }

  if (state.error) {
    return (
      <div className="social-alert error" data-testid="social-error">
        <AlertTriangle size={16} />
        <div>
          <strong>{state.error}</strong>
          <button className="social-btn ghost" onClick={load} data-testid="social-retry">
            <RefreshCw size={13} /> Reintentar
          </button>
        </div>
      </div>
    );
  }

  const data = state.data || {};
  const channels = data.channels || [];
  const needsReconnect = data.status === 'needs_reconnect';

  if (!data.connected && !needsReconnect) {
    return (
      <div className="social-empty" data-testid="social-empty">
        <div className="social-empty-badge">
          <Link2 size={22} />
        </div>
        <h3>Conectá tus redes para publicar directamente desde Studio Media Labs.</h3>
        <p className="social-note">
          Buffer permite conectar y administrar tus cuentas sociales.
          Necesitás una cuenta de Buffer para utilizar esta función.
        </p>
        {data.provider_configured === false && (
          <div className="social-alert warning" data-testid="social-not-configured">
            <AlertTriangle size={15} />
            <span>
              Falta configurar las credenciales de Buffer en el servidor.
              Avisale al administrador de Studio Media Labs.
            </span>
          </div>
        )}
        <button
          className="social-btn primary"
          onClick={connect}
          disabled={busy === 'connect' || data.provider_configured === false}
          data-testid="social-connect-btn"
        >
          {busy === 'connect'
            ? <><Loader2 size={15} className="animate-spin" /> Abriendo Buffer…</>
            : <><Link2 size={15} /> Conectar con Buffer</>}
        </button>
        <a
          className="social-link"
          href="https://buffer.com/signup"
          target="_blank"
          rel="noopener noreferrer"
        >
          Crear una cuenta de Buffer <ExternalLink size={12} />
        </a>
      </div>
    );
  }

  return (
    <div className="social-connected" data-testid="social-connected">
      <div className="social-account-card">
        <div className="social-account-main">
          <span className={`social-state-dot ${needsReconnect ? 'warn' : 'ok'}`} />
          <div>
            <h3 data-testid="social-account-name">
              {data.organizations?.[0]?.name || data.account_email || 'Cuenta de Buffer'}
            </h3>
            <p>
              {needsReconnect
                ? 'La autorización venció: reconectá para seguir publicando.'
                : `Conectado vía Buffer${data.account_email ? ` · ${data.account_email}` : ''}`}
            </p>
          </div>
        </div>
        <div className="social-account-actions">
          <button className="social-btn ghost" onClick={sync} disabled={busy === 'sync'}
                  data-testid="social-sync-btn">
            {busy === 'sync' ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />}
            Sincronizar
          </button>
          <button className="social-btn ghost" onClick={connect} disabled={busy === 'connect'}
                  data-testid="social-reconnect-btn">
            <Link2 size={13} /> Reconectar
          </button>
          <button className="social-btn danger" onClick={disconnect} disabled={busy === 'disconnect'}
                  data-testid="social-disconnect-btn">
            <Unlink size={13} /> Desconectar
          </button>
        </div>
      </div>

      <div className="social-channels-head">
        <h4>Canales disponibles</h4>
        <span>{channels.length}</span>
      </div>

      {channels.length === 0 ? (
        <div className="social-alert warning" data-testid="social-no-channels">
          <AlertTriangle size={15} />
          <span>
            Todavía no hay canales. Conectá tus redes dentro de Buffer y volvé a
            sincronizar desde acá.
          </span>
        </div>
      ) : (
        <ul className="social-channel-list" data-testid="social-channel-list">
          {channels.map((channel) => (
            <li key={channel.id} className={`social-channel ${channel.status}`}
                data-testid={`social-channel-${channel.id}`}>
              <div className="social-channel-avatar">
                {channel.avatar_url
                  ? <img src={channel.avatar_url} alt={channel.display_name || channel.platform} />
                  : <PlatformIcon platform={channel.platform} size={18} />}
              </div>
              <div className="social-channel-info">
                <span className="social-channel-name">
                  {channel.display_name || channel.username || platformMeta(channel.platform).label}
                </span>
                <span className="social-channel-meta">
                  <PlatformIcon platform={channel.platform} size={11} />
                  {platformMeta(channel.platform).label}
                  {channel.username ? ` · ${channel.username}` : ''}
                </span>
              </div>
              <span className={`social-chip ${channel.status === 'connected' ? 'ok' : 'warn'}`}>
                {channel.status === 'connected'
                  ? <CheckCircle2 size={11} /> : <AlertTriangle size={11} />}
                {CHANNEL_STATUS_LABEL[channel.status] || channel.status}
              </span>
            </li>
          ))}
        </ul>
      )}

      <p className="social-note small">
        Buffer permite conectar y administrar tus cuentas sociales. Para agregar o
        quitar redes, hacelo en Buffer y después presioná «Sincronizar».
      </p>
    </div>
  );
};

export default SocialConnectionPanel;
